/**
 * Stage Manager — derives the HITL flow from the pipeline, not a fixed stage list.
 *
 * The flow is always:
 *   1. Validate — review & correct the extracted, normalized data (editable per uiGroup).
 *   2..N. Deliver — one stage per enabled pipeline output (pipeline.outputs[]), showing that
 *         destination's mapped record + enrichment; each is submitted independently.
 *   N+1. Complete — delivery done; advance to the next document of the same model.
 *
 * Removing an output removes its stage; there are no hardcoded stage names. "Back" from a
 * Deliver stage returns to Validate (never a committed output) so re-review can't double-submit.
 */

import { useState, useEffect, useMemo } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import Alert from '@mui/material/Alert';
import DataValidationStage from './DataValidationStage';
import DeliverOutputStage from './DeliverOutputStage';
import SuccessStage from './SuccessStage';
import documentClassificationService from '../../../services/documentClassificationService';
import { useDocumentService } from '../../../services/documentService';
import { OutputStep } from '../../../types';

interface StageManagerProps {
  extractedData: any;
  documentId: string;
  onNextDocument?: (nextDocumentId: string) => void;
  /** When opened from the exception dashboard, restricts next-document navigation to the same group. */
  sourceGroup?: 'stranded' | 'failed' | 'onHold';
}

type HitlStage =
  | { kind: 'validate'; name: string; description: string }
  | { kind: 'deliver'; name: string; description: string; output: OutputStep }
  | { kind: 'complete'; name: string; description: string };

export default function StageManager({ extractedData, documentId, onNextDocument, sourceGroup }: StageManagerProps) {
  const { updateDocumentStatus } = useDocumentService();
  const [currentStageIndex, setCurrentStageIndex] = useState(0);
  const [stages, setStages] = useState<HitlStage[]>([]);
  const [documentType, setDocumentType] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [finalizeError, setFinalizeError] = useState<string | null>(null);
  const [processedData, setProcessedData] = useState<any>(null);

  useEffect(() => {
    async function loadStages() {
      try {
        // 1. Resolve document type (classify if metadata is missing).
        let docType = extractedData?.metadata?.documentType;
        if (!docType) {
          const classification = documentClassificationService.classifyFromExtractedData(extractedData);
          if (!documentClassificationService.isSupported(classification.documentType)) {
            setError(
              `Unsupported document type: ${documentClassificationService.getDisplayName(classification.documentType)}. ` +
              `Confidence: ${Math.round(classification.confidence * 100)}%`
            );
            setLoading(false);
            return;
          }
          docType = classification.documentType;
          if (extractedData.metadata) {
            extractedData.metadata.documentType = docType;
            extractedData.metadata.classification = classification;
          }
        }
        setDocumentType(docType);

        // 2. Load the pipeline and derive stages from its enabled outputs.
        const pipelineService = (await import('../../../services/pipelineService')).default;
        const pipeline = await pipelineService.getPipeline(docType);
        const enabledOutputs: OutputStep[] = (pipeline?.outputs || []).filter((o) => o?.enabled);

        const built: HitlStage[] = [
          {
            kind: 'validate',
            name: 'Validate',
            description: 'Review and correct the extracted, normalized data',
          },
          ...enabledOutputs.map<HitlStage>((output, i) => ({
            kind: 'deliver',
            name: output.label || `Output ${i + 1}`,
            description:
              output.mode === 'api'
                ? `Review and submit to ${output.destinationConnectorId}`
                : `Review and queue for batch export to ${output.destinationConnectorId}`,
            output,
          })),
          {
            kind: 'complete',
            name: 'Complete',
            description: 'Delivery complete — continue to the next document',
          },
        ];

        setStages(built);
        setError(null);
      } catch (err) {
        console.error('Failed to load HITL stages:', err);
        setError(err instanceof Error ? err.message : 'Failed to load validation stages');
      } finally {
        setLoading(false);
      }
    }

    loadStages();
  }, [extractedData]);

  const currentStage = useMemo(() => stages[currentStageIndex] || null, [stages, currentStageIndex]);

  const progress = useMemo(() => {
    if (stages.length === 0) return 0;
    return ((currentStageIndex + 1) / stages.length) * 100;
  }, [currentStageIndex, stages.length]);

  const advance = () => {
    setCurrentStageIndex((i) => (i < stages.length - 1 ? i + 1 : i));
  };

  // Validate → carry corrected data forward and move to the first Deliver (or Complete).
  // With no deliver stages there is no output submit to finalize the document server-side,
  // so tell the backend validation is done (PATCH VALIDATED) — it fans out to the pipeline's
  // outputs (none) and marks the document COMPLETED itself. The browser never sets terminal
  // status directly (UX3). If that call fails we STAY on Validate with a visible error —
  // advancing would show a success screen for a document the backend never finalized.
  const handleValidateSubmit = async (data: any) => {
    setProcessedData(data);
    if (!stages.some((s) => s.kind === 'deliver')) {
      try {
        setFinalizeError(null);
        await updateDocumentStatus(documentId, 'VALIDATED');
      } catch (err) {
        console.error('[StageManager] Failed to record validation completion:', err);
        setFinalizeError(
          'Could not record the validation on the server. Your corrections are saved — check your connection and press Submit again.'
        );
        return;
      }
    }
    advance();
  };

  // Deliver → this output is committed (server-side dupe-guard makes any re-submit a no-op); move on.
  const handleOutputSubmitted = (_outputId: string) => {
    advance();
  };

  // From a Deliver stage, "Back" always returns to Validate — prior outputs are already
  // committed and must not be re-submitted.
  const backToValidate = () => setCurrentStageIndex(0);

  if (loading) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 2 }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">Loading validation stages...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Alert severity="error" sx={{ maxWidth: 600 }}>
          <Typography variant="h6" gutterBottom>Stage Configuration Error</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{error}</Typography>
        </Alert>
      </Box>
    );
  }

  if (!currentStage) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body1" color="text.secondary">No stages configured for this document type</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Progress indicator */}
      <Paper elevation={0} sx={{ borderBottom: 1, borderColor: 'divider', px: 2, py: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" fontWeight={600}>
            Stage {currentStageIndex + 1} of {stages.length}: {currentStage.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {Math.round(progress)}% Complete
          </Typography>
        </Box>
        <LinearProgress variant="determinate" value={progress} sx={{ height: 6, borderRadius: 3 }} />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          {currentStage.description}
        </Typography>
      </Paper>

      {/* Validation-finalize failure (zero-output pipelines) — stay on Validate, allow retry */}
      {finalizeError && currentStage.kind === 'validate' && (
        <Alert severity="error" sx={{ mx: 2, mt: 1 }}>
          {finalizeError}
        </Alert>
      )}

      {/* Render current stage */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        {currentStage.kind === 'validate' && (
          <DataValidationStage
            extractedData={processedData || extractedData}
            documentId={documentId}
            onSubmit={handleValidateSubmit}
          />
        )}
        {currentStage.kind === 'deliver' && (
          <DeliverOutputStage
            output={currentStage.output}
            documentId={documentId}
            extractedData={processedData || extractedData}
            onSubmitted={handleOutputSubmitted}
            onBack={backToValidate}
          />
        )}
        {currentStage.kind === 'complete' && (
          <SuccessStage
            documentId={documentId}
            documentType={documentType}
            onNextDocument={onNextDocument}
            sourceGroup={sourceGroup}
          />
        )}
      </Box>
    </Box>
  );
}
